<script setup>
/**
 * Tooltip — drop-in replacement for BV2's b-tooltip.
 *
 * Wraps the trigger element in the default slot; shows tooltip text on hover.
 *
 * Props:
 *   title     — tooltip text
 *   placement — 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 *   variant   — 'dark' | 'light' (default: 'dark')
 */
import { ref, nextTick, useSlots } from 'vue';

const props = defineProps({
    title:     { type: String,  default: '' },
    placement: { type: String,  default: 'top' },
    variant:   { type: String,  default: 'dark' },
});

const slots      = useSlots();
const show       = ref(false);
const anchor     = ref(null);
const tooltipEl  = ref(null);
const tooltipStyle = ref({});

const GAP = 2;

async function onEnter() {
    if (!props.title && !slots.content) return;
    show.value = true;
    await nextTick();
    positionTooltip();
}

function onLeave() {
    show.value = false;
    tooltipStyle.value = {};
}

function positionTooltip() {
    if (!anchor.value || !tooltipEl.value) return;

    const anchorTarget = anchor.value.firstElementChild || anchor.value;
    const a = anchorTarget.getBoundingClientRect();
    const t = tooltipEl.value.getBoundingClientRect();

    let top, left;

    if (props.placement === 'top') {
        top  = a.top - t.height - GAP;
        left = a.left + a.width / 2 - t.width / 2;
    } else if (props.placement === 'bottom') {
        top  = a.bottom + GAP;
        left = a.left + a.width / 2 - t.width / 2;
    } else if (props.placement === 'left') {
        top  = a.top + a.height / 2 - t.height / 2;
        left = a.left - t.width - GAP;
    } else if (props.placement === 'right') {
        top  = a.top + a.height / 2 - t.height / 2;
        left = a.right + GAP;
    }

    tooltipStyle.value = {
        position:   'fixed',
        top:        top + 'px',
        left:       left + 'px',
        zIndex:     1070,
        transform:  'none',
        willChange: 'unset',
        margin:     '0',
    };
}
</script>

<template>
    <span class="tooltip-trigger" @mouseenter="onEnter" @mouseleave="onLeave">
        <span ref="anchor" class="tooltip-anchor">
            <slot />
        </span>

        <div
            v-if="show"
            ref="tooltipEl"
            role="tooltip"
            :class="['tooltip', 'b-tooltip', `bs-tooltip-${placement}`, variant === 'light' ? 'tooltip-light' : '']"
            :style="tooltipStyle"
        >
            <div
                class="arrow"
                :style="placement === 'left' || placement === 'right'
                    ? 'top: 50%; transform: translateY(-50%);'
                    : 'left: 50%; transform: translateX(-50%);'"
            ></div>
            <div class="tooltip-inner">
                <slot name="content">{{ title }}</slot>
            </div>
        </div>
    </span>
</template>

<style scoped>
.tooltip-trigger,
.tooltip-anchor {
    display: inline-flex;
    align-items: center;
    width: fit-content;
}

/* Center arrow for left/right placements */
.bs-tooltip-right .arrow,
.bs-tooltip-left .arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin-top: 0 !important;
}

/* Light variant arrow colors */
.tooltip-light.bs-tooltip-right .arrow::before {
    border-right-color: #fff !important;
}
.tooltip-light.bs-tooltip-left .arrow::before {
    border-left-color: #fff !important;
}
.tooltip-light.bs-tooltip-top .arrow::before {
    border-top-color: #fff !important;
}
.tooltip-light.bs-tooltip-bottom .arrow::before {
    border-bottom-color: #fff !important;
}
</style>
